import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Save, FileDown, FileX, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../api';
import { authFetch } from '../../components/MedicationExtras';
import { FaceSheetForm } from '../../components/facesheet/FaceSheetForm';
import { FACESHEET_LAYOUT } from '../../utils/facesheetLayout';

const LAYOUT_KEYS = new Set(FACESHEET_LAYOUT.flatMap(s => s.rows.flat().flatMap(c => c.keys || [c.k])));
import { buildFacesheetPdf, chartDefaults, medsText } from '../../utils/facesheetPdf';
import { AGENCY, AGENCY_CONTACT_LINE } from '../../utils/agencyInfo';

const flat = (v) => (Array.isArray(v) ? v.filter(Boolean).map(x => (typeof x === 'object' ? Object.values(x).filter(Boolean).join(' ') : x)).join(', ') : v ?? '');
const isoDate = (v) => {
  const s = String(v || '');
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : s.slice(0, 10);
};
const ageOf = (dob) => {
  const d = new Date(dob);
  if (!dob || Number.isNaN(d.getTime())) return '';
  const n = new Date();
  return String(n.getFullYear() - d.getFullYear() - (n < new Date(n.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0));
};
const usDate = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v || ''); };

// Face sheet values pre-filled from the chart (same mapping the PDF uses)
const fromChart = (p, meds) => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
  const base = chartDefaults({ patientName: name, chart: { ...p, currentMedications: meds } });
  const v = Object.fromEntries(Object.entries(base).map(([k, x]) => [k, flat(x)]));
  return {
    ...v, medications: medsText(meds || ''), sex: p.gender || '', apt: p.address2 || '',
    dateOfBirth: isoDate(p.dateOfBirth), insuranceEffectiveDate: isoDate(p.insuranceEffectiveDate),
    age: ageOf(p.dateOfBirth), referralSource: p.referralSource || '', homeboundStatus: p.homeboundStatus || '',
  };
};

export default function PatientFaceSheetTemplate() {
  const [params] = useSearchParams();
  const patientId = params.get('patientId');
  const [patient, setPatient] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    (async () => {
      try {
        const r = await api.get(`/patients/${patientId}`);
        const p = r.data.patient || r.data;
        const m = await authFetch(`/medications/patient/${patientId}`).then(x => x.json()).catch(() => null);
        const meds = m?.medications?.length ? m.medications.filter(x => (x.status || 'active').toLowerCase() !== 'discontinued') : p.currentMedications;
        const saved = await api.get(`/admission-forms/${patientId}/patient_facesheet`).then(x => x.data?.form?.data || {}).catch(() => ({}));
        const raw = saved.data && typeof saved.data === 'object' ? saved.data : saved;
        // ignore a saved sheet that belongs to someone else's name (old test data)
        const sameName = !raw.lastName || String(raw.lastName).toLowerCase() === String(p.lastName || '').toLowerCase();
        const kept = sameName ? Object.fromEntries(Object.entries(raw).filter(([k, x]) => LAYOUT_KEYS.has(k) && x !== '' && x != null && typeof x !== 'object')) : {};
        setPatient(p);
        // the chart is the source of truth; saved face sheet only fills what the chart lacks
        const chart = Object.fromEntries(Object.entries(fromChart(p, meds)).filter(([, x]) => x !== '' && x != null));
        setValues({ ...kept, ...chart });
      } catch {
        toast.error('Could not load this patient');
      }
    })();
  }, [patientId]);

  const set = (k, x) => setValues(prev => ({ ...prev, [k]: x, ...(k === 'dateOfBirth' ? { age: ageOf(x) } : {}) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(values).filter(([k]) => LAYOUT_KEYS.has(k)));
      const r = await api.post('/admission-forms/save', { ...body, gender: values.sex, patientId, formType: 'patient_facesheet' });
      toast.success(r.data.patientUpdated ? 'Face Sheet saved and the patient chart updated' : 'Face Sheet saved');
    } catch { toast.error('Could not save the Face Sheet'); }
    finally { setSaving(false); }
  };

  const download = (mode) => {
    const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
    const doc = buildFacesheetPdf(mode === 'blank' ? {} : values, { patientName: name, chart: patient });
    doc.fillMode = mode;
    doc.save(`Face_Sheet_${name.replace(/\s+/g, '_')}${mode === 'blank' ? '_blank' : ''}.pdf`);
  };

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto p-6 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3" data-testid="facesheet-no-patient">
          <AlertCircle className="w-6 h-6 text-yellow-600" /> Open a patient chart first, then choose Face Sheet.
        </div>
      </DashboardLayout>
    );
  }

  const btn = 'flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50';
  return (
    <DashboardLayout>
      <div className="max-w-[1000px] mx-auto" data-testid="patient-facesheet-form">
        <div className="flex flex-wrap justify-end gap-2 mb-3 print:hidden">
          <button onClick={() => download('blank')} disabled={!patient} className={`${btn} border border-gray-300 bg-white hover:bg-gray-50`} data-testid="facesheet-blank-btn"><FileX className="w-4 h-4" /> Blank PDF</button>
          <button onClick={() => download('prefill')} disabled={!patient} className={`${btn} bg-white border border-[#1e40af] text-[#1e40af] hover:bg-blue-50`} data-testid="facesheet-download-btn"><FileDown className="w-4 h-4" /> Download PDF</button>
          <button onClick={save} disabled={!patient || saving} className={`${btn} bg-[#1e40af] text-white hover:bg-[#1e3a8a]`} data-testid="save-form-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {!patient ? <p className="text-center text-gray-500 py-16">Loading…</p> : (
          <FaceSheetForm values={values} onChange={set} header={(
            <div className="flex flex-col sm:flex-row items-start justify-between gap-2 sm:gap-4 mb-2 font-serif">
              <div className="flex items-center gap-3">
                <img src="/images/ivory-logo.png" alt="IVORY" className="w-14 h-14 rounded-full" />
                <div className="font-sans">
                  <div className="font-bold text-[15px]">{AGENCY.name.toUpperCase()}</div>
                  <div className="text-[11px] text-gray-700">{AGENCY.address}, {AGENCY.cityStateZip}</div>
                  <div className="text-[11px] text-gray-700">{AGENCY_CONTACT_LINE}</div>
                </div>
              </div>
              <div className="text-[#1e40af] font-bold text-2xl tracking-wide self-center">FACE SHEET</div>
              <div className="sm:text-right text-[12px] leading-6">
                <div>Admission Date: {usDate(values.admissionDate)}</div>
                <div data-testid="facesheet-mrn">MR#: {patient.medicalRecordNumber || ''}</div>
              </div>
            </div>
          )} />
        )}
      </div>
    </DashboardLayout>
  );
}
